import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Divider, Icon, Menu, MenuItem, Select, Tag, TextField, Typography } from "@bookips/sds";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { getUserAddress } from "src/api/address";
import { formatDateToDot } from "src/utils/formatting";
import { AddressManagementDialog } from "../Address/AddressManagementDialog";
import { DropDownBox } from "../DropDownBox";
import useDeliveryCoupon from "../hooks/useDeliveryCoupon";
import { RadiusBoxStyle } from "../index";
import { QueryKeys } from "../queries/useAddressManagement";
import { formatPhoneNumberToDash } from "../utils/formatNumber";
// 이름 검증 유틸리티 함수
const validateName = (value)=>{
    // 이모지, 특수문자 제거 (한글, 영문, 숫자, 공백 허용)
    let filteredValue = value.replace(/[^\w가-힣\s]/g, "");
    // 숫자만 또는 공백만으로 구성된 경우 차단
    if (/^\d+$/.test(filteredValue) || /^\s+$/.test(filteredValue)) {
        filteredValue = "";
    }
    return {
        filteredValue,
        isValid: filteredValue.length > 0,
        errors: {
            notOnlyNumbers: /^\d+$/.test(value) ? "숫자만으로는 입력할 수 없습니다." : null,
            notOnlySpaces: /^\s+$/.test(value) ? "공백만으로는 입력할 수 없습니다." : null,
            invalidCharacters: !/^[\w가-힣\s]+$/.test(value) ? "한글, 영문, 숫자만 입력 가능합니다." : null
        }
    };
};
// 배송 상품의 경우, 주문자 정보 섹션, 배송지 섹션 두 개로 나뉘어져 있음
export const DeliveryUserInfoSection = ()=>{
    var _errors_buyerName, _errors_buyerPhone;
    // 이 섹션에서만 쓰이는 펼침 상태이므로 내부에서 소유한다
    const [customDropDown, setCustomDropDown] = useState(false);
    const handleCustomDropDown = ()=>setCustomDropDown((state)=>!state);
    const { register , watch , setValue , trigger , formState: { errors  }  } = useFormContext();
    const phone = watch("buyerPhone");
    const formName = watch("buyerName");
    const handlePhoneChange = (e)=>{
        const formatted = formatPhoneNumberToDash(e.target.value);
        setValue("buyerPhone", formatted);
    };
    const checkNameChange = (e)=>{
        const { filteredValue  } = validateName(e.currentTarget.value);
        if (e.currentTarget.value !== filteredValue) {
            e.currentTarget.value = filteredValue;
            setValue("buyerName", filteredValue);
            trigger("buyerName");
        }
    };
    useEffect(()=>{
        // 필수값이 누락되고 드롭다운이 접혀있다면(true) 드롭다운 열기
        if ((errors.buyerName || errors.buyerPhone) && customDropDown) {
            handleCustomDropDown();
        }
    }, [
        errors
    ]);
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsx(DropDownBox, {
            headTitle: /*#__PURE__*/ _jsxs(Typography, {
                as: "span",
                variant: "h6",
                className: "flex items-center gap-1.5",
                children: [
                    "주문자",
                    formName && customDropDown && /*#__PURE__*/ _jsxs(Typography, {
                        variant: "body1",
                        className: "text-secondary",
                        children: [
                            formName,
                            " / ",
                            formatPhoneNumberToDash(phone)
                        ]
                    })
                ]
            }),
            bodyElement: /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-4",
                children: [
                    /*#__PURE__*/ _jsx(TextField, {
                        labelText: "이름 *",
                        placeholder: "주문자 이름을 입력해주세요.",
                        size: "lg",
                        value: formName,
                        error: !!errors.buyerName,
                        helperText: (_errors_buyerName = errors.buyerName) === null || _errors_buyerName === void 0 ? void 0 : _errors_buyerName.message,
                        onChange: checkNameChange,
                        maxLength: 20,
                        ...register("buyerName", {
                            required: "필수 입력 사항이에요.",
                            maxLength: {
                                value: 20,
                                message: "20자 이하로 입력해주세요."
                            },
                            validate: {
                                notOnlyNumbers: (value)=>validateName(value).errors.notOnlyNumbers || true,
                                notOnlySpaces: (value)=>validateName(value).errors.notOnlySpaces || true,
                                validCharacters: (value)=>validateName(value).errors.invalidCharacters || true
                            }
                        })
                    }),
                    /*#__PURE__*/ _jsx(TextField, {
                        labelText: "전화번호 *",
                        placeholder: "주문자 전화번호를 입력해주세요.",
                        size: "lg",
                        value: formatPhoneNumberToDash(phone),
                        error: !!errors.buyerPhone,
                        helperText: (_errors_buyerPhone = errors.buyerPhone) === null || _errors_buyerPhone === void 0 ? void 0 : _errors_buyerPhone.message,
                        onChange: handlePhoneChange,
                        ...register("buyerPhone", {
                            required: "필수 입력 사항이에요."
                        })
                    })
                ]
            }),
            openDropDown: customDropDown,
            dropDownMode: true,
            handleDropDown: ()=>handleCustomDropDown()
        })
    });
};
// 배송지 정보 섹션
export const DeliveryAddressSection = (param)=>{
    let { enabled , orderType , deliveryFeeCouponId  } = param;
    const [open, setOpen] = useState(false);
    const { register , setValue , formState: { errors  }  } = useFormContext();
    const { data: userAddress  } = useQuery({
        queryKey: [
            QueryKeys.ADDRESS_LIST
        ],
        queryFn: ()=>getUserAddress(),
        enabled: enabled
    });
    const { deliveryCoupons , handleChangeDeliveryFeeCoupon  } = useDeliveryCoupon({
        isLoggedIn: enabled,
        orderType: orderType
    });
    const { recipientName , phone , address1 , address2 , deliveryNote , addressName  } = useMemo(()=>{
        var _userAddress_find;
        return (_userAddress_find = userAddress === null || userAddress === void 0 ? void 0 : userAddress.find((address)=>address.isDefault)) !== null && _userAddress_find !== void 0 ? _userAddress_find : {
            recipientName: "",
            phone: "",
            address1: "",
            address2: "",
            deliveryNote: "",
            addressName: ""
        };
    }, [
        userAddress
    ]);
    // 배송지 정보가 있을 때 폼에 값 설정
    useEffect(()=>{
        if (recipientName && phone && address1 && address2) {
            setValue("recipientName", recipientName);
            setValue("recipientPhone", phone);
            setValue("address1", address1);
            setValue("address2", address2);
            setValue("message", deliveryNote);
            setValue("addressName", addressName);
        }
    }, [
        recipientName,
        phone,
        address1,
        address2,
        deliveryNote,
        addressName,
        setValue
    ]);
    return /*#__PURE__*/ _jsxs(RadiusBoxStyle, {
        className: "flex flex-col gap-6 max-sm:rounded-none",
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "h6",
                children: "배송지"
            }),
            recipientName && phone && address1 && address2 && /*#__PURE__*/ _jsxs("div", {
                className: "flex justify-between",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-col gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs(Typography, {
                                variant: "subtitle1",
                                className: "flex gap-2",
                                children: [
                                    recipientName,
                                    /*#__PURE__*/ _jsx(Typography, {
                                        as: "span",
                                        variant: "subtitle1",
                                        className: "text-secondary",
                                        children: addressName
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "body1",
                                className: "text-secondary",
                                children: phone
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "body1",
                                        children: address1
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "body1",
                                        children: address2
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "body1",
                                className: "text-secondary",
                                children: deliveryNote
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        variant: "stroke",
                        size: "sm",
                        className: "w-fit",
                        color: "secondary",
                        onClick: ()=>{
                            setOpen((state)=>!state);
                        },
                        children: "배송지 관리"
                    })
                ]
            }),
            !address1 && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        variant: "fill",
                        size: "lg",
                        className: "w-full",
                        color: "secondary",
                        leftElement: /*#__PURE__*/ _jsx(Icon.Form_2px, {
                            className: "size-5"
                        }),
                        onClick: ()=>{
                            setOpen(true);
                        },
                        children: "배송지 입력"
                    }),
                    errors.address1 && /*#__PURE__*/ _jsx(Typography, {
                        variant: "body2",
                        className: "text-status-error",
                        children: "배송지를 입력해주세요."
                    })
                ]
            }),
            (deliveryCoupons === null || deliveryCoupons === void 0 ? void 0 : deliveryCoupons.coupons.length) > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-6",
                children: [
                    /*#__PURE__*/ _jsx(Divider, {}),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-col gap-3",
                        children: [
                            /*#__PURE__*/ _jsxs(Typography, {
                                variant: "h6",
                                className: "flex items-center justify-between gap-2",
                                children: [
                                    "배송비 쿠폰",
                                    /*#__PURE__*/ _jsx(Tag, {
                                        color: "red",
                                        size: "md",
                                        bold: true,
                                        children: "쿠폰 적용중"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Select, {
                                size: "lg",
                                fillContainer: true,
                                value: deliveryFeeCouponId,
                                onValueChange: (value)=>handleChangeDeliveryFeeCoupon(value),
                                triggerProps: {
                                    className: "outline-none h-full bg-transparent hover:bg-transparent shadow-none focus:shadow-none px-3 py-[9px] hover:bg-gray-300/20 data-[state=open]:bg-gray-300/20  text-primary"
                                },
                                children: /*#__PURE__*/ _jsx(Menu, {
                                    children: deliveryCoupons.coupons.map((coupon)=>/*#__PURE__*/ _jsx(MenuItem, {
                                            value: coupon.id,
                                            children: /*#__PURE__*/ _jsxs(Typography, {
                                                variant: "body1",
                                                children: [
                                                    coupon.name,
                                                    " ",
                                                    "(~".concat(formatDateToDot(coupon.expiredAt.toString()), ")")
                                                ]
                                            })
                                        }, coupon.id))
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(AddressManagementDialog, {
                open: open,
                onClose: ()=>setOpen(false),
                listData: userAddress
            })
        ]
    });
};
